import { type InputProps, NumberInput } from '@library/components/inputs';
import { classNames } from '@library/utils';
import { useCallback } from 'react';

export interface AdjustmentAmountInputProps extends InputProps {
  error?: string;
  id?: string;
  labelClassName?: string;
  labelName?: string;
  limitAmount?: number | undefined;
  onAdjustmentAmountValueChange: ({
    value,
    error
  }: {
    value: string;
    error: string;
  }) => void;
  placeholder?: string;
  value: string;
}

export const AdjustmentAmountInput = ({
  error = '',
  id = 'adjustmentAmount',
  labelClassName = 'text-sm rounded-md leading-6 text-soClean-180 w-full',
  labelName = 'Adjustment Amount',
  limitAmount = undefined,
  onAdjustmentAmountValueChange,
  placeholder = '0.00',
  value,
  ...rest
}: AdjustmentAmountInputProps) => {
  // Temporarily commented out until we have a more clear use case for this
  // const formattedLimitAmount = limitAmount ? limitAmount.toFixed(2) : undefined;

  const minLimitAmount = limitAmount ? -limitAmount : undefined;

  const handleBlur = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      const formattedValue = e.target.value
        ? Number(e.target.value).toFixed(2).toString()
        : '';

      if (formattedValue && formattedValue !== value && !error) {
        onAdjustmentAmountValueChange({ value: formattedValue, error });
      }
    },
    [error, onAdjustmentAmountValueChange, value]
  );

  const handleOnChange = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      let validationError = '';

      if (!e.target.validity.valid) {
        if (e.target.validity.valueMissing) {
          validationError = 'Value is required.';
        } else if (
          e.target.validity.rangeUnderflow ||
          e.target.validity.rangeOverflow
        ) {
          validationError = `Amount falls outside the authorization level.`;
        } else {
          validationError = 'Invalid input.';
        }
      }

      onAdjustmentAmountValueChange({
        value: e.target.value,
        error: validationError
      });
    },
    [onAdjustmentAmountValueChange]
  );

  return (
    <div className="flex w-full flex-col gap-2">
      {labelName && (
        <label htmlFor={id} className={labelClassName}>
          {labelName}
        </label>
      )}
      <div
        className={classNames(
          'flex w-full items-center justify-between rounded-md border px-2 py-1',
          error ? 'border-roses' : 'border-soFresh-140'
        )}
        data-testid="adjustment-amount"
      >
        <div className="flex flex-1 items-center">
          <span className="text-soClean-100 bg-soFresh-140 border-soFresh-140 rounded-md border px-2 text-sm">
            $
          </span>
          <NumberInput
            className="font-heebo bg-soFresh-110 text-soClean-100 flex-1 flex-col justify-center rounded-md border-none text-sm font-medium leading-normal tracking-tight"
            data-testid="adjustment-amount-input"
            id={id}
            max={limitAmount}
            min={minLimitAmount}
            step="0.01"
            value={value}
            placeholder={placeholder}
            onBlur={handleBlur}
            onChange={handleOnChange}
            {...rest}
          />
        </div>
        {/* Temporarily commented out until we have a more clear use case for this
        limitAmount && (
          <p
            className="text-soClean-180 text-sm"
            data-testid="adjusment-amount-limits"
          >
            Limit $-{formattedLimitAmount} to {formattedLimitAmount}
          </p>
        ) */}
      </div>
      {error && (
        <p
          className="text-roses text-sm"
          data-testid="adjusment-amount-validation-error"
        >
          {error}
        </p>
      )}
    </div>
  );
};
